import moment from 'moment';
import {
  AgeBracket, ChecklistData, ChecklistStageData, Region,
} from './checklistData';

/** Recursively replace the {pet} token in any string with the active pet's name. */
export function fillPet<T>(value: T, name: string): T {
  if (typeof value === 'string') {
    return value.split('{pet}').join(name) as unknown as T;
  }
  if (Array.isArray(value)) {
    return value.map((v) => fillPet(v, name)) as unknown as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      out[k] = fillPet(v, name);
    });
    return out as unknown as T;
  }
  return value;
}

export interface StageStats {
  done: number;
  total: number;
  started: boolean;
}

export interface ChecklistStats {
  stats: StageStats[];
  done: number;
  total: number;
  percent: number;
}

export type CheckedMap = Record<string, boolean>;

/** Per-stage done/total/started plus overall percent (equal weight per task). */
export function computeStats(data: ChecklistData, checked: CheckedMap): ChecklistStats {
  let done = 0;
  let total = 0;
  const stats = data.stages.map((stage: ChecklistStageData) => {
    const stageDone = stage.tasks.filter((t) => checked[t.id]).length;
    done += stageDone;
    total += stage.tasks.length;
    return { done: stageDone, total: stage.tasks.length, started: stageDone > 0 };
  });
  const percent = total === 0 ? 0 : Math.round((done / total) * 100);
  return {
    stats, done, total, percent,
  };
}

/** Pets under 6 months get the puppy/kitten variant of conditional lessons. */
export function getPetAgeBracket(birthDate?: string | null): AgeBracket {
  if (!birthDate) return 'over6';
  const months = moment().diff(moment(birthDate), 'months');
  return months < 6 ? 'under6' : 'over6';
}

/**
 * Region for partner-CTA gating (24Pet microchip: US+CA, pet insurance: US only).
 * awo.country stores full names ('USA', 'Canada', 'Australia'); unknown defaults
 * to US, the primary market.
 */
export function getRegion(country?: string | null): Region {
  if (!country) return 'US';
  const normalized = country.toLowerCase();
  if (normalized === 'canada') return 'CA';
  if (normalized === 'australia') return 'INTL';
  return 'US';
}
